import React, { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../utils/api'
import { CaseDetailContext } from './CaseDetailContext'

/**
 * CaseDelete — port of Scale's CaseDelete.jsx.
 * Permanently deletes a case + related records. Mediator's Case Manager can
 * trigger the same delete flow on behalf of the mediator.
 */
export default function CaseDelete({ onToast }) {
    const { caseData } = useContext(CaseDetailContext)
    const navigate = useNavigate()

    const [dialogIsOpen, setDialogIsOpen] = useState(false)
    const [loading, setLoading] = useState(false)

    const handleDelete = async () => {
        if (!caseData?.id) return
        try {
            setLoading(true)
            await api.delete(`/cases/${caseData.id}`)
            setDialogIsOpen(false)
            setLoading(false)
            onToast?.({ type: 'success', message: 'Case deleted successfully' })
            // The toast is rendered on this case page, so navigating away immediately
            // unmounts it before it can show. Give it a moment first — same delayed-
            // navigate pattern CaseCancel/TransferCase use.
            setTimeout(() => navigate('/cases'), 1200)
        } catch (err) {
            setLoading(false)
            console.error('Failed to delete case:', err)
            onToast?.({ type: 'error', message: 'Failed to delete case' })
        }
    }

    return (
        <div className="w-full m-0 p-0 h-full flex items-stretch">
            <button
                type="button"
                onClick={() => setDialogIsOpen(true)}
                className="bg-white border border-gray-300 text-gray-600 rounded-md h-11 px-4 py-2 flex justify-between cursor-pointer items-center w-full hover:bg-gray-50 transition-colors"
            >
                <span className="text-sm font-medium">Delete Case</span>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" className="h-5 w-5 flex-shrink-0 text-gray-500" fill="currentColor">
                    <path d="M95.193,442.491c0,28.806,23.481,52.236,52.338,52.236h217.031c28.806,0,52.243-23.43,52.243-52.236V153.225h7.642 c16.443,0,29.826-13.376,29.826-29.819v-10.229c0-21.718-17.623-39.385-39.29-39.385h-66.333V46.028 c0-15.853-12.902-28.755-28.763-28.755H192.104c-15.86,0-28.763,12.902-28.763,28.755v27.765H97.008 c-21.659,0-39.283,17.667-39.283,39.385v10.229c0,16.443,13.376,29.819,29.819,29.819h7.65V442.491z M401.886,442.491 c0,20.574-16.742,37.316-37.323,37.316H147.532c-20.632,0-37.418-16.742-37.418-37.316V153.225h291.772V442.491z M178.261,46.028 c0-7.628,6.207-13.835,13.842-13.835h127.786c7.635,0,13.842,6.207,13.842,13.835v27.765h-155.47V46.028z M72.645,123.406v-10.229 c0-13.493,10.928-24.464,24.362-24.464h317.978c13.434,0,24.37,10.972,24.37,24.464v10.229c0,8.218-6.688,14.899-14.906,14.899 c-67.212,0-269.89,0-336.905,0C79.326,138.305,72.645,131.624,72.645,123.406z"/>
                    <path d="M195.958 421.116c4.124 0 7.46-3.337 7.46-7.46V219.376c0-4.124-3.337-7.46-7.46-7.46s-7.46 3.337-7.46 7.46v194.279C188.497 417.779 191.834 421.116 195.958 421.116zM255.996 421.116c4.124 0 7.46-3.337 7.46-7.46V219.376c0-4.124-3.337-7.46-7.46-7.46s-7.46 3.337-7.46 7.46v194.279C248.536 417.779 251.873 421.116 255.996 421.116zM316.035 421.116c4.124 0 7.46-3.337 7.46-7.46V219.376c0-4.124-3.337-7.46-7.46-7.46-4.124 0-7.46 3.337-7.46 7.46v194.279C308.575 417.779 311.912 421.116 316.035 421.116z"/>
                </svg>
            </button>

            {dialogIsOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" onClick={() => !loading && setDialogIsOpen(false)}>
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="border-b pb-2"><h5 className="text-lg font-semibold mb-1">Delete Case</h5></div>
                        <p className="text-gray-900 font-semibold text-md mt-4">
                            Delete Permanently deletes the case and all associated data. Parties will be notified. This action cannot be undone.
                        </p>
                        <div className="w-full flex justify-between gap-3 mt-6">
                            <button type="button" disabled={loading} className="w-full bg-[#EBEBEB] text-black font-semibold rounded-xl py-3" onClick={() => setDialogIsOpen(false)}>Cancel</button>
                            <button type="button" disabled={loading} onClick={handleDelete} className="w-full bg-[#FC4043] text-white rounded-xl py-3 font-semibold disabled:opacity-60">
                                {loading ? 'Deleting…' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
